/**
 * LaTeX Insertion Helpers
 * Helper functions for inserting LaTeX commands and formatting at cursor position
 */

import { EditorView } from '@codemirror/view'
import { EditorSelection } from '@codemirror/state'
import { logDebug } from '@utils/logger'

/**
 * Get the current selection or cursor position
 */
export function getSelection(view: EditorView): { from: number; to: number; text: string } {
  const selection = view.state.selection.main
  const from = selection.from
  const to = selection.to
  const text = view.state.doc.sliceString(from, to)
  return { from, to, text }
}

/**
 * Get the current line boundaries and text
 */
export function getCurrentLine(view: EditorView): { from: number; to: number; text: string } {
  const selection = view.state.selection.main
  const pos = selection.head
  const line = view.state.doc.lineAt(pos)
  return {
    from: line.from,
    to: line.to,
    text: line.text
  }
}

/**
 * Check if cursor is inside a LaTeX command (e.g., \textbf{...})
 * This is a simplified check - it looks for the command before the cursor
 */
export function isInsideCommand(view: EditorView, commandName: string): boolean {
  const { from } = getSelection(view)
  const text = view.state.doc.sliceString(0, from)
  
  // Look for the command pattern before cursor
  // Match: \command{...} where cursor is inside the braces
  const pattern = new RegExp(`\\\\${commandName}\\{([^}]*)$`)
  const match = pattern.test(text)
  
  // Also check if we're right after the command (before opening brace)
  const afterPattern = new RegExp(`\\\\${commandName}\\{?$`)
  const afterMatch = afterPattern.test(text)
  
  return match || afterMatch
}

/**
 * Wrap selected text in a LaTeX command, or insert command template if no selection
 */
export function wrapInCommand(
  view: EditorView,
  commandName: string,
  from?: number,
  to?: number
): void {
  const selection = getSelection(view)
  const start = from ?? selection.from
  const end = to ?? selection.to
  const selectedText = selection.text

  if (selectedText) {
    // Wrap existing selection
    const insertText = `\\${commandName}{${selectedText}}`
    const cursorPos = start + insertText.length

    view.dispatch({
      changes: {
        from: start,
        to: end,
        insert: insertText
      },
      selection: EditorSelection.cursor(cursorPos)
    })
  } else {
    // Insert command template with cursor inside braces
    const insertText = `\\${commandName}{}`
    const cursorPos = start + insertText.length - 1 // Position before closing brace

    view.dispatch({
      changes: {
        from: start,
        to: end,
        insert: insertText
      },
      selection: EditorSelection.cursor(cursorPos)
    })
  }

  logDebug('[InsertionHelpers] Wrapped in command', { commandName, start, end })
}

/**
 * Insert bold formatting
 */
export function insertBold(view: EditorView): void {
  wrapInCommand(view, 'textbf')
}

/**
 * Insert italic formatting
 */
export function insertItalic(view: EditorView): void {
  wrapInCommand(view, 'textit')
}

/**
 * Insert underline formatting
 */
export function insertUnderline(view: EditorView): void {
  wrapInCommand(view, 'underline')
}

/**
 * Insert a document structure command (section, subsection, etc.)
 */
export function insertStructure(
  view: EditorView,
  structureType: 'section' | 'subsection' | 'subsubsection' | 'paragraph' | 'subparagraph'
): void {
  const line = getCurrentLine(view)
  const lineText = line.text.trim()
  
  // If line has content, wrap it in the structure command
  if (lineText) {
    // Remove existing structure commands if present
    const cleanedText = lineText.replace(/^\\(section|subsection|subsubsection|paragraph|subparagraph)\{([^}]+)\}$/, '$2')
    const insertText = `\\${structureType}{${cleanedText}}`
    
    view.dispatch({
      changes: {
        from: line.from,
        to: line.to,
        insert: insertText
      },
      selection: EditorSelection.cursor(line.from + insertText.length)
    })
  } else {
    // Insert structure command at cursor
    const { from } = getSelection(view)
    const insertText = `\\${structureType}{}`
    const cursorPos = from + insertText.length - 1
    
    view.dispatch({
      changes: {
        from,
        to: from,
        insert: insertText
      },
      selection: EditorSelection.cursor(cursorPos)
    })
  }

  logDebug('[InsertionHelpers] Inserted structure', { structureType })
}

/**
 * Toggle comment on selected lines or current line
 * In LaTeX, comments start with %
 */
export function toggleComment(view: EditorView): void {
  const { from, to } = getSelection(view)
  const doc = view.state.doc
  
  // Get all lines in the selection
  const fromLine = doc.lineAt(from)
  const toLine = doc.lineAt(to)
  
  const changes: Array<{ from: number; to: number; insert: string }> = []
  let allCommented = true
  
  // Check if all lines are commented
  for (let lineNum = fromLine.number; lineNum <= toLine.number; lineNum++) {
    const line = doc.line(lineNum)
    const trimmed = line.text.trim()
    if (trimmed && !trimmed.startsWith('%')) {
      allCommented = false
      break
    }
  }
  
  // Toggle comments on all lines
  for (let lineNum = fromLine.number; lineNum <= toLine.number; lineNum++) {
    const line = doc.line(lineNum)
    const trimmed = line.text.trim()
    
    if (allCommented) {
      // Uncomment: remove % from start of line
      if (trimmed.startsWith('%')) {
        const commentIndex = line.text.indexOf('%')
        // Check if % is at the start or after whitespace
        if (commentIndex >= 0 && (commentIndex === 0 || line.text.slice(0, commentIndex).trim() === '')) {
          // Remove % and one space if present
          const afterComment = line.text.slice(commentIndex + 1)
          const newText = afterComment.startsWith(' ') ? afterComment.slice(1) : afterComment
          changes.push({
            from: line.from,
            to: line.to,
            insert: newText
          })
        }
      }
    } else {
      // Comment: add % at the start of non-empty lines
      if (trimmed && !trimmed.startsWith('%')) {
        // Find first non-whitespace character
        const firstNonWhitespace = line.text.search(/\S/)
        if (firstNonWhitespace >= 0) {
          changes.push({
            from: line.from + firstNonWhitespace,
            to: line.from + firstNonWhitespace,
            insert: '% '
          })
        }
      }
    }
  }
  
  if (changes.length > 0) {
    view.dispatch({
      changes,
      selection: EditorSelection.create([
        EditorSelection.range(
          from + (allCommented ? -2 : 2),
          to + (allCommented ? -2 : 2)
        )
      ])
    })
  }
  
  logDebug('[InsertionHelpers] Toggled comment', { allCommented, lines: toLine.number - fromLine.number + 1 })
}

/**
 * Insert a LaTeX environment (itemize, enumerate, etc.)
 */
export function insertEnvironment(
  view: EditorView,
  envName: string,
  options?: { indentLevel?: number; itemCount?: number }
): void {
  const { from, to } = getSelection(view)
  const indentLevel = options?.indentLevel ?? 0
  const itemCount = options?.itemCount ?? 1
  const indent = '  '.repeat(indentLevel) // 2 spaces per indent level
  
  // Build environment with items
  let items = ''
  for (let i = 0; i < itemCount; i++) {
    items += `${indent}  \\item \n`
  }
  
  const insertText = `\\begin{${envName}}\n${items}${indent}\\end{${envName}}`
  
  // Calculate cursor position (first \item after \begin)
  const beginLength = `\\begin{${envName}}\n${indent}  \\item `.length
  const cursorPos = from + beginLength

  view.dispatch({
    changes: {
      from,
      to,
      insert: insertText
    },
    selection: EditorSelection.cursor(cursorPos)
  })

  logDebug('[InsertionHelpers] Inserted environment', { envName, indentLevel, itemCount })
}

/**
 * Insert itemize environment
 */
export function insertItemize(view: EditorView, itemCount: number = 1): void {
  insertEnvironment(view, 'itemize', { itemCount })
}

/**
 * Insert enumerate environment
 */
export function insertEnumerate(view: EditorView, itemCount: number = 1): void {
  insertEnvironment(view, 'enumerate', { itemCount })
}

